import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Button,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { axiosGetCall } from "src/services/apisCall";

const columns = [
  { id: "title", label: "Title", align: "start" },
  { id: "createdByName", label: "Created By", align: "center" },
  { id: "status", label: "Status", align: "center" },
  { id: "createdAt", label: "Created At", align: "center" },
  { id: "startDate", label: "Start Date", align: "center" },
  { id: "endDate", label: "End Date", align: "center" },
  { id: "meetType", label: "Meeting Type", align: "center" },
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date) ? "-" : date.toLocaleDateString();
};

const extractNameFromTitle = (title) => {
  return title?.split(/[-:|]/)[0]?.trim() || title;
};

const UpcomingMeetingTable = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [clearFilter, setClearFilter] = useState(false);

  const fetchUpcomingMeetings = async (page, limit, startDate, endDate) => {
    setLoading(true);
    try {
      const skip = page * limit;
      const params = { skip, limit };
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      const { result = [], total = 0 } = await axiosGetCall("upcomingMeetList", params);

      const formattedRows = result?.map((doc) => ({
        id: doc._id,
        title: extractNameFromTitle(doc.title),
        createdByName: doc.createdByName || "-",
        status: doc.status || "-",
        createdAt: formatDate(doc.createdAt),
        startDate: formatDate(doc.startDate),
        endDate: formatDate(doc.endDate),
        meetType: doc.meetType || "-",
      }));

      setRows(formattedRows);
      setTotalCount(total);
    } catch (error) {
      console.error("Error fetching upcoming meetings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcomingMeetings(page, rowsPerPage, dateRange.startDate, dateRange.endDate);
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApplyFilter = () => {
    setClearFilter(true)
    fetchUpcomingMeetings(0, rowsPerPage, dateRange.startDate, dateRange.endDate);
    setPage(0);
  };

  const handleClearFilter = () => {
    setClearFilter(false)
    setDateRange({ startDate: null, endDate: null });
    fetchUpcomingMeetings(0, rowsPerPage, null, null);
    setPage(0);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                  mb: 2,
                  mx: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <DatePicker
                    label="Start Date"
                    value={dateRange.startDate}
                    onChange={(newValue) =>
                      setDateRange((prev) => ({ ...prev, startDate: newValue }))
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: 140,
                          ".MuiInputBase-input": { p: 1 },
                          '& label': {
                            zIndex: 0, 
                      },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="End Date"
                    value={dateRange.endDate}
                    onChange={(newValue) =>
                      setDateRange((prev) => ({ ...prev, endDate: newValue }))
                    }
                    minDate={dateRange.startDate}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: 140,
                          ".MuiInputBase-input": { p: 1 },
                          '& label': {
                            zIndex: 0, 
                   },
                        },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleApplyFilter}
                    disabled={!dateRange.startDate && !dateRange.endDate} 
                   sx={{ alignSelf: "center", whiteSpace: "nowrap" }}
                  >
                    Apply Filter
                  </Button>
                  {clearFilter && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleClearFilter}
                      sx={{ alignSelf: "center", whiteSpace: "nowrap" }}
                    >
                      Clear Filter
                    </Button>
                  )}
                </Box>
      
                <Box sx={{ fontWeight: "bold", mt: { xs: 2, sm: 0 } }}>
                  Total: {totalCount}
                </Box>
              </Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                flex: 1,
                overflowY: "auto",
                maxHeight: "calc(100vh - 300px)",
                position: "relative",
              }}
            >
              <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableHead sx={{ position: "sticky", top: 0, backgroundColor: "white" }}>
                  <TableRow>
                    {columns?.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          width: `${100 / columns.length}%`,
                          fontWeight: "bold",
                          padding: "8px",
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} sx={{ height: "300px", padding: 0 }}>
                        <Box
                          sx={{
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            color: "black",
                            fontStyle: "italic",
                            fontSize: "1.5rem",
                          }}
                        >
                          Data not found
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows?.map((row) => (
                      <TableRow key={row.id}>
                        {columns?.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{
                              width: `${100 / columns.length}%`,
                              padding: "8px 20px",
                              borderBottom: ".5px dotted rgba(0, 0, 0, 0.1)",
                            }}
                          >
                            {row[column.id]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default UpcomingMeetingTable;
